import { GeoCoordinates, GroupedSuggestions, Item, Suggestion } from '@borg/types';
import { isEmptyString, isNumber, isString, toggleElementInArray, truncDigits, expandDeduplicatedSuggestions, DeduplicatedSuggestion, deduplicateSuggestionsByName } from '@borg/utils';
import { injectHistory } from '@/components/JobSearchBar/useHistory';

type Dropdown =
  | 'none'
  | 'positions-menu'
  | 'locations-menu'
  | 'positions-autocomplete'
  | 'locations-autocomplete';

const injectionKey: InjectionKey<ReturnType<typeof useSearchBar>> = Symbol('searchbar');

export function provideSearchBar() {
  const searchbar = useSearchBar();
  provide(injectionKey, searchbar);
  return searchbar;
}

export function injectSearchBar() {
  const searchbar = inject(injectionKey);

  if (!searchbar) {
    throw new Error('Injection Error: Searchbar not provided');
  }

  return searchbar;
}

function useSearchBar() {
  let positionKeyword = '';
  let locationKeyword = '';
  const jobsSearchStore = useJobsSearchStore();
  const history = injectHistory();
  const dropdownTarget = ref<Dropdown>('none');
  const positionsGroupedSuggestions = ref<GroupedSuggestions>({ primary: [], secondary: [] });
  const locationGroupedSuggestions = ref<GroupedSuggestions>({ primary: [], secondary: [] });
  // keep positions and keywords separate - only deduplicate actual position suggestions
  const selectedPositions = ref<Suggestion[]>([
    ...deduplicateSuggestionsByName(jobsSearchStore.filters.static.positions),
    ...jobsSearchStore.filters.static.query.map((q) => ({ id: q, name: q })),
  ]);
  // initialize with deduplicated locations from store
  const initialLocations = deduplicateSuggestionsByName(jobsSearchStore.filters.static.locations);
  const selectedLocations = ref<DeduplicatedSuggestion<Suggestion>[]>(initialLocations);

  function syncStateWithStore(storeState: typeof jobsSearchStore.filters.static) {
    const { positions, locations, query } = storeState;

    // for positions: deduplicate position suggestions but keep keywords as-is
    const deduplicatedPositions = positions.length > 0 ? deduplicateSuggestionsByName(positions) : [];
    const keywordSuggestions = query.map((q) => ({ id: q, name: q }));
    selectedPositions.value = [...deduplicatedPositions, ...keywordSuggestions];

    // for locations from store, deduplicate them for UI display
    selectedLocations.value = locations.length > 0
      ? deduplicateSuggestionsByName(locations)
      : [];
  }

  async function initPositions() {
    // extract numeric position IDs, expanding deduplicated ones
    const positionIds: number[] = [];
    selectedPositions.value.forEach((item) => {
      if ('originalIds' in item) {
        // this is a deduplicated position, expand it
        item.originalIds.forEach((id) => {
          if (isNumber(id)) {
            positionIds.push(id);
          }
        });
      } else if (isNumber(item.id)) {
        // this is a regular position or keyword with numeric ID
        positionIds.push(item.id);
      }
      // skip string ids i.e. keywords
    });

    const result = await positionsService.getSuggestions({
      positions: positionIds,
    });

    if (result.isSuccess) {
      positionsGroupedSuggestions.value = result.data;
    }
  }

  async function initLocations(coordinates?: GeoCoordinates) {
    const result = await locationsService.getSuggestions({
      lat: coordinates?.lat,
      lng: coordinates?.lng,
      locations: selectedLocations.value.map((s) => s.id),
    });

    if (result.isSuccess) {
      locationGroupedSuggestions.value = result.data;
    }
  }

  function isPositionSelected(suggestion: Suggestion | DeduplicatedSuggestion<Suggestion>) {
    return selectedPositions.value.some((item) => {
      if ('originalIds' in item && 'originalIds' in suggestion) {
        return item.id === suggestion.id;
      } else if ('originalIds' in item) {
        return item.originalIds.includes(suggestion.id) || item.id === suggestion.id;
      } else if ('originalIds' in suggestion) {
        return suggestion.originalIds.includes(item.id) || item.id === suggestion.id;
      }
      return item.id === suggestion.id;
    });
  }

  function isLocationSelected(suggestion: DeduplicatedSuggestion<Suggestion>) {
    return selectedLocations.value.some((item) => item.id === suggestion.id);
  }

  function togglePosition(position: Suggestion | DeduplicatedSuggestion<Suggestion>) {
    selectedPositions.value = toggleElementInArray(
      selectedPositions.value as any[],
      position,
      (item) => {
        if ('originalIds' in item && 'originalIds' in position) {
          return item.id === position.id;
        } else if ('originalIds' in item) {
          return item.originalIds.includes(position.id) || item.id === position.id;
        } else if ('originalIds' in position) {
          return position.originalIds.includes(item.id) || item.id === position.id;
        }
        return item.id === position.id;
      },
    ) as any;
  }

  function toggleLocation(location: DeduplicatedSuggestion<Suggestion>) {
    selectedLocations.value = toggleElementInArray(
      selectedLocations.value,
      location,
      (item) => item.id === location.id,
    );
  }

  function createKeyword(text: string) {
    selectedPositions.value.push({
      id: text,
      name: text,
    });
  }

  function open(target: Dropdown) {
    dropdownTarget.value = target;
  }

  function close() {
    dropdownTarget.value = 'none';
  }

  function search() {
    // Separate keywords and positions from deduplicated data
    const keywords: Item<string>[] = [];
    const deduplicatedPositions: Item<number>[] = [];

    selectedPositions.value.forEach((item) => {
      if ('originalIds' in item) {
        // This is a deduplicated position, use the primary suggestion for store/URL
        const primarySuggestion = item.suggestions[0];
        if (isNumber(primarySuggestion.id)) {
          deduplicatedPositions.push(primarySuggestion as Item<number>);
        } else {
          keywords.push(primarySuggestion as Item<string>);
        }
      } else if (isString(item.id)) {
        // This is a keyword
        keywords.push(item as Item<string>);
      } else if (isNumber(item.id)) {
        // This is a regular position
        deduplicatedPositions.push(item as Item<number>);
      }
    });

    // Use deduplicated locations for store/URL (not expanded)
    const deduplicatedLocationsForStore = selectedLocations.value.map(loc => ({
      id: loc.name, // Use name as ID for locations in URLs
      name: loc.name
    }));

    jobsSearchStore.setStaticFilterValues({
      query: keywords.map((s) => s.name),
      positions: deduplicatedPositions,
      locations: deduplicatedLocationsForStore,
    });

    navigate({ name: 'search', query: getSearchQueryObject(jobsSearchStore.filters) });
  }

  function getDropdownElement() {
    return document.querySelector('#mp-header-dropdown');
  }

  function togglePointed() {
    const lastPointedElem =
      getDropdownElement()?.querySelectorAll<HTMLElement>('.option--pointed')[0];
    if (lastPointedElem) {
      (lastPointedElem.firstElementChild as HTMLElement)?.click();
    }

    return lastPointedElem;
  }

  function move(direction: 'forward' | 'backward') {
    const dropdownElement = getDropdownElement();
    if (!dropdownElement) return;

    const optionElements = Array.from(dropdownElement.querySelectorAll('.option'));
    if (optionElements.length === 0) return;

    const firstElement = optionElements[0];
    const lastElement = optionElements[optionElements.length - 1];
    const lastPointedOptionElement = dropdownElement.querySelectorAll('.option--pointed')[0];
    const lastPointedOptionElementIndex = optionElements.findIndex(
      (e) => e === lastPointedOptionElement,
    );

    if (direction === 'forward') {
      if (!lastPointedOptionElement) {
        firstElement.classList.add('option--pointed');
      } else if (lastPointedOptionElementIndex === optionElements.length - 1) {
        lastPointedOptionElement.classList.remove('option--pointed');
        firstElement.classList.add('option--pointed');
      } else {
        lastPointedOptionElement.classList.remove('option--pointed');
        optionElements[lastPointedOptionElementIndex + 1].classList.add('option--pointed');
      }
    } else {
      if (!lastPointedOptionElement) {
        lastElement.classList.add('option--pointed');
      } else if (lastPointedOptionElementIndex === 0) {
        lastPointedOptionElement.classList.remove('option--pointed');
        lastElement.classList.add('option--pointed');
      } else {
        lastPointedOptionElement.classList.remove('option--pointed');
        optionElements[lastPointedOptionElementIndex - 1].classList.add('option--pointed');
      }
    }
  }

  function onKeyDown(event: KeyboardEvent, source: 'positions' | 'locations') {
    switch (event.key) {
      case 'Escape':
        close();
        break;
      case 'ArrowDown':
        move('forward');
        break;
      case 'ArrowUp':
        move('backward');
        break;
      case 'Backspace':
        if (source === 'positions' && isEmptyString(positionKeyword)) {
          open('positions-menu');
        } else if (source === 'locations' && isEmptyString(locationKeyword)) {
          open('locations-menu');
        }
        break;
      case 'Enter':
        const pointed = togglePointed();
        if (
          !pointed &&
          ((source === 'positions' && !positionKeyword) ||
            (source === 'locations' && !locationKeyword))
        ) {
          search();
          close();
        }
        break;
      case 'Meta':
      case 'Control':
      case 'Shift':
      case 'Alt':
        break;
      default:
        open(source === 'positions' ? 'positions-autocomplete' : 'locations-autocomplete');
        break;
    }
  }

  function scrollToTheEndOfInput(inputName: string) {
    const input = document.querySelector<HTMLInputElement>(`input[name='${inputName}']`);
    if (input) {
      input.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  function onUpdatePositionKeyword(keyword?: string) {
    positionKeyword = keyword || '';
    if (!positionKeyword) {
      close();
      setTimeout(() => {
        scrollToTheEndOfInput('positions');
      }, 300);
    }
  }

  function onUpdateLocationKeyword(keyword?: string) {
    locationKeyword = keyword || '';
    if (!locationKeyword) {
      close();
      setTimeout(() => {
        scrollToTheEndOfInput('locations');
      }, 300);
    }
  }

  watch(() => jobsSearchStore.filters.static, syncStateWithStore, { immediate: true });

  watch(dropdownTarget, (target, previousTarget) => {
    if (previousTarget === 'positions-menu') {
      initPositions();
    } else if (previousTarget === 'locations-menu') {
      initLocations(myGeoLocationService.getLastResolved());
    }
    if (target === 'none') {
      history.init();
    }
  });

  return reactive({
    dropdownTarget,
    positionsGroupedSuggestions,
    locationGroupedSuggestions,
    selectedPositions,
    selectedLocations,
    initPositions,
    initLocations,
    isLocationSelected,
    isPositionSelected,
    togglePosition,
    toggleLocation,
    onUpdatePositionKeyword,
    onUpdateLocationKeyword,
    open,
    close,
    search,
    onKeyDown,
    createKeyword,
  });
}
